import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, TrendingUp, Users, DollarSign, Building, Target, Award, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { landingTranslations } from '../translations/landingTranslations';
import Navbar from '../components/Navbar';
import './SectorDetailPage.css';

interface SectorData {
  id: string;
  title: string;
  description: string;
  heroImage: string;
  overview: string;
  advantages: string[];
  opportunities: string[];
  requirements: string[];
  support: string[];
  statistics: {
    label: string;
    value: string;
  }[];
  contactPerson: {
    name: string;
    position: string;
    email: string;
    phone: string;
  };
}

const SectorDetailPage: React.FC = () => {
  const { sectorId } = useParams<{ sectorId: string }>();
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const t = landingTranslations[currentLanguage] || landingTranslations.id;

  // Scroll to top when component mounts or sectorId changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [sectorId]);

  const sectorDataMap: { [key: string]: SectorData } = {
    'guitar-industry': {
      id: 'guitar-industry',
      title: currentLanguage === 'id' ? 'Industri Gitar di Baki' : 'Guitar Industry in Baki',
      description: currentLanguage === 'id' 
        ? 'Pusat manufaktur gitar terkenal dunia dengan potensi ekspor yang besar'
        : 'World-renowned guitar manufacturing center with massive export potential',
      heroImage: 'https://picsum.photos/1200/600?random=20',
      overview: currentLanguage === 'id'
        ? 'Kabupaten Sukoharjo, khususnya di Kecamatan Baki, telah menjadi pusat industri gitar terbesar di Indonesia. Dengan lebih dari 200 unit usaha kecil dan menengah yang memproduksi gitar akustik dan elektrik, industri ini telah mengekspor produknya ke berbagai negara termasuk Amerika Serikat, Eropa, dan Asia.'
        : 'Sukoharjo Regency, specifically in Baki District, has become the largest guitar industry center in Indonesia. With more than 200 small and medium enterprises producing acoustic and electric guitars, this industry has exported its products to various countries including the United States, Europe, and Asia.',
      advantages: currentLanguage === 'id' ? [
        'Tenaga kerja terampil dalam pembuatan gitar',
        'Akses mudah ke bahan baku kayu berkualitas',
        'Jaringan supplier dan distributor yang established',
        'Biaya produksi yang kompetitif',
        'Dukungan pemerintah daerah yang kuat',
        'Lokasi strategis untuk distribusi domestik dan ekspor'
      ] : [
        'Skilled workforce in guitar manufacturing',
        'Easy access to quality wood raw materials',
        'Established supplier and distributor networks',
        'Competitive production costs',
        'Strong local government support',
        'Strategic location for domestic distribution and export'
      ],
      opportunities: currentLanguage === 'id' ? [
        'Pengembangan produk gitar premium dan custom',
        'Integrasi teknologi digital dalam produksi',
        'Ekspansi pasar ekspor ke negara-negara baru',
        'Pengembangan produk aksesoris gitar',
        'Kerjasama dengan brand internasional',
        'Pengembangan wisata industri musik'
      ] : [
        'Development of premium and custom guitar products',
        'Integration of digital technology in production',
        'Export market expansion to new countries',
        'Development of guitar accessory products',
        'Collaboration with international brands',
        'Development of music industry tourism'
      ],
      requirements: currentLanguage === 'id' ? [
        'Modal investasi minimum Rp 500 juta',
        'Pengalaman dalam industri musik atau woodworking',
        'Komitmen terhadap kualitas dan standar internasional',
        'Sertifikasi ISO untuk produk ekspor'
      ] : [
        'Minimum investment capital of IDR 500 million',
        'Experience in music industry or woodworking',
        'Commitment to quality and international standards',
        'ISO certification for export products'
      ],
      support: currentLanguage === 'id' ? [
        'Bantuan perizinan one-stop service',
        'Akses ke pusat pelatihan tenaga kerja',
        'Dukungan promosi dalam pameran internasional',
        'Fasilitas kawasan industri dengan infrastruktur lengkap'
      ] : [
        'One-stop service licensing assistance',
        'Access to workforce training centers',
        'Promotional support in international exhibitions',
        'Industrial area facilities with complete infrastructure'
      ],
      statistics: [
        {
          label: currentLanguage === 'id' ? 'Unit Usaha' : 'Business Units',
          value: '200+'
        },
        {
          label: currentLanguage === 'id' ? 'Tenaga Kerja' : 'Workforce',
          value: '5,000+'
        },
        {
          label: currentLanguage === 'id' ? 'Negara Tujuan Ekspor' : 'Export Destination Countries',
          value: '15+'
        },
        {
          label: currentLanguage === 'id' ? 'Nilai Ekspor/Tahun' : 'Annual Export Value',
          value: '$2M+'
        }
      ],
      contactPerson: {
        name: 'Ahmad Solihin',
        position: currentLanguage === 'id' ? 'Kepala Seksi Industri' : 'Head of Industry Section',
        email: 'industri@sukoharjokab.go.id',
        phone: '+62 271 593156'
      }
    },
    'textile-industry': {
      id: 'textile-industry',
      title: currentLanguage === 'id' ? 'Industri Tekstil' : 'Textile Industry',
      description: currentLanguage === 'id'
        ? 'Sektor manufaktur tekstil dan garmen yang berkembang pesat'
        : 'Rapidly growing textile and garment manufacturing sector',
      heroImage: 'https://picsum.photos/1200/600?random=21',
      overview: currentLanguage === 'id'
        ? 'Industri tekstil di Sukoharjo berkembang pesat dengan fokus pada produksi kain batik, konveksi, dan produk tekstil lainnya. Didukung oleh ketersediaan tenaga kerja yang terampil dan akses yang baik ke pasar domestik dan internasional.'
        : 'The textile industry in Sukoharjo is growing rapidly with a focus on batik fabric production, garment manufacturing, and other textile products. Supported by skilled workforce availability and good access to domestic and international markets.',
      advantages: currentLanguage === 'id' ? [
        'Tradisi batik yang kuat dan otentik',
        'Tenaga kerja terampil dalam tekstil',
        'Akses ke bahan baku lokal dan impor',
        'Infrastruktur logistik yang baik',
        'Dukungan program pengembangan UMKM',
        'Pasar domestik yang besar'
      ] : [
        'Strong and authentic batik tradition',
        'Skilled workforce in textiles',
        'Access to local and imported raw materials',
        'Good logistics infrastructure',
        'MSME development program support',
        'Large domestic market'
      ],
      opportunities: currentLanguage === 'id' ? [
        'Pengembangan produk fashion modern dengan motif tradisional',
        'Ekspansi ke pasar fashion internasional',
        'Integrasi teknologi digital printing',
        'Pengembangan produk ramah lingkungan',
        'Kerjasama dengan desainer internasional'
      ] : [
        'Development of modern fashion products with traditional motifs',
        'Expansion to international fashion markets',
        'Digital printing technology integration',
        'Development of eco-friendly products',
        'Collaboration with international designers'
      ],
      requirements: currentLanguage === 'id' ? [
        'Modal investasi minimum Rp 300 juta',
        'Pengetahuan tentang industri tekstil',
        'Komitmen terhadap kualitas produk',
        'Sertifikasi lingkungan untuk produk ramah lingkungan'
      ] : [
        'Minimum investment capital of IDR 300 million',
        'Knowledge of textile industry',
        'Commitment to product quality',
        'Environmental certification for eco-friendly products'
      ],
      support: currentLanguage === 'id' ? [
        'Pelatihan desain dan produksi batik',
        'Bantuan akses pasar dan pameran',
        'Dukungan sertifikasi produk',
        'Fasilitas kawasan industri tekstil'
      ] : [
        'Batik design and production training',
        'Market access and exhibition assistance',
        'Product certification support',
        'Textile industrial area facilities'
      ],
      statistics: [
        {
          label: currentLanguage === 'id' ? 'Unit Usaha' : 'Business Units',
          value: '150+'
        },
        {
          label: currentLanguage === 'id' ? 'Tenaga Kerja' : 'Workforce',
          value: '3,000+'
        },
        {
          label: currentLanguage === 'id' ? 'Produk Utama' : 'Main Products',
          value: '10+'
        },
        {
          label: currentLanguage === 'id' ? 'Nilai Produksi/Tahun' : 'Annual Production Value',
          value: '$1.5M+'
        }
      ],
      contactPerson: {
        name: 'Siti Nurhayati',
        position: currentLanguage === 'id' ? 'Kepala Seksi Tekstil' : 'Head of Textile Section',
        email: 'tekstil@sukoharjokab.go.id',
        phone: '+62 271 593157'
      }
    },
    // Add more sectors following the same pattern...
  };

  const sectorData = sectorDataMap[sectorId || ''];

  if (!sectorData) {
    return (
      <div className="sector-detail-page">
        {/* Navbar */}
        <Navbar isScrolled={true} />
        
        <div className="container">
          <div className="not-found">
            <h2>{currentLanguage === 'id' ? 'Sektor tidak ditemukan' : 'Sector not found'}</h2>
            <p>{currentLanguage === 'id' ? 'Gunakan navigasi di atas untuk kembali.' : 'Use the navigation above to go back.'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sector-detail-page">
      {/* Navbar */}
      <Navbar isScrolled={true} />
      
      {/* Header */}
      <div className="sector-header">
        <div className="container">
          <div className="sector-hero">
            <div className="hero-content">
              <h1 className="sector-title">{sectorData.title}</h1>
              <p className="sector-description">{sectorData.description}</p>
            </div>
            <div className="hero-image">
              <img src={sectorData.heroImage} alt={sectorData.title} />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="sector-content">
        <div className="container">
          <div className="content-grid">
            {/* Main Content */}
            <div className="main-content">
              {/* Overview */}
              <section className="content-section">
                <h2 className="section-title">
                  <Building className="w-6 h-6" />
                  {currentLanguage === 'id' ? 'Gambaran Umum' : 'Overview'}
                </h2>
                <p className="section-description">{sectorData.overview}</p>
              </section>

              {/* Advantages */}
              <section className="content-section">
                <h2 className="section-title">
                  <TrendingUp className="w-6 h-6" />
                  {currentLanguage === 'id' ? 'Keunggulan' : 'Advantages'}
                </h2>
                <ul className="advantage-list">
                  {sectorData.advantages.map((advantage, index) => (
                    <li key={index} className="advantage-item">
                      <CheckCircle className="w-5 h-5" />
                      <span>{advantage}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Opportunities */}
              <section className="content-section">
                <h2 className="section-title">
                  <Target className="w-6 h-6" />
                  {currentLanguage === 'id' ? 'Peluang Investasi' : 'Investment Opportunities'}
                </h2>
                <ul className="opportunity-list">
                  {sectorData.opportunities.map((opportunity, index) => (
                    <li key={index} className="opportunity-item">
                      <Award className="w-5 h-5" />
                      <span>{opportunity}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Requirements */}
              <section className="content-section">
                <h2 className="section-title">
                  <DollarSign className="w-6 h-6" />
                  {currentLanguage === 'id' ? 'Persyaratan Investasi' : 'Investment Requirements'}
                </h2>
                <ul className="requirement-list">
                  {sectorData.requirements.map((requirement, index) => (
                    <li key={index} className="requirement-item">
                      <span>{requirement}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Support */}
              <section className="content-section">
                <h2 className="section-title">
                  <Users className="w-6 h-6" />
                  {currentLanguage === 'id' ? 'Dukungan Pemerintah' : 'Government Support'}
                </h2>
                <ul className="support-list">
                  {sectorData.support.map((support, index) => (
                    <li key={index} className="support-item">
                      <CheckCircle className="w-5 h-5" />
                      <span>{support}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            {/* Sidebar */}
            <div className="sidebar">
              {/* Statistics */}
              <div className="statistics-card">
                <h3>{currentLanguage === 'id' ? 'Statistik Sektor' : 'Sector Statistics'}</h3>
                <div className="stats-grid">
                  {sectorData.statistics.map((stat, index) => (
                    <div key={index} className="stat-item">
                      <div className="stat-value">{stat.value}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="contact-card">
                <h3>{currentLanguage === 'id' ? 'Kontak Person' : 'Contact Person'}</h3>
                <div className="contact-info">
                  <div className="contact-name">{sectorData.contactPerson.name}</div>
                  <div className="contact-position">{sectorData.contactPerson.position}</div>
                  <div className="contact-details">
                    <div className="contact-item">
                      <span>Email:</span>
                      <a href={`mailto:${sectorData.contactPerson.email}`}>
                        {sectorData.contactPerson.email}
                      </a>
                    </div>
                    <div className="contact-item">
                      <span>Phone:</span>
                      <a href={`tel:${sectorData.contactPerson.phone}`}>
                        {sectorData.contactPerson.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="cta-card">
                <h3>{currentLanguage === 'id' ? 'Tertarik Berinvestasi?' : 'Interested in Investing?'}</h3>
                <p>
                  {currentLanguage === 'id' 
                    ? 'Hubungi tim kami untuk diskusi lebih lanjut mengenai peluang investasi di sektor ini.'
                    : 'Contact our team for further discussion about investment opportunities in this sector.'
                  }
                </p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/contact')}
                >
                  {currentLanguage === 'id' ? 'Hubungi Kami' : 'Contact Us'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectorDetailPage;